import { useEffect, useState } from 'react';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { ROLE_ORDER, type UserRole, type AppUser } from '@shared/types';
import type { UsersListResponse, UserWriteResponse } from '@shared/api-responses';

const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

export default function Users() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; msg: string } | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    apiFetch<UsersListResponse>('/users')
      .then((r) => setUsers(r.users))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load users'))
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  async function patch(u: AppUser, body: { role?: UserRole; is_active?: boolean }) {
    setBusyId(u.id);
    setRowError(null);
    try {
      const res = await apiFetch<UserWriteResponse>(`/users/${u.id}`, { method: 'PUT', body });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.user : x)));
    } catch (e) {
      setRowError({ id: u.id, msg: e instanceof Error ? e.message : 'Failed to save' });
    } finally {
      setBusyId(null);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="rounded-lg bg-amber-50 text-amber-800 px-4 py-3 text-sm">
        User management is restricted to super admins.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-dcvfd-dark">Users &amp; Roles</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Assign roles and activate/deactivate accounts. New users start as <strong>Viewer</strong> on first sign-in.
          You can&rsquo;t change your own account.
        </p>
      </div>

      {error && <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
      {loading && <p className="text-sm text-zinc-400 animate-pulse">Loading…</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface-raised">
          <table className="w-full text-sm">
            <thead className="bg-surface text-zinc-500 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = !!user?.email && u.email === user.email;
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} className="border-t border-border-subtle align-top">
                    <td className="px-4 py-2 font-medium text-dcvfd-dark">
                      {u.name}
                      {isSelf && <span className="ml-1 text-xs text-zinc-400">(you)</span>}
                    </td>
                    <td className="px-4 py-2 text-zinc-600">{u.email ?? '—'}</td>
                    <td className="px-4 py-2">
                      <select
                        value={u.role}
                        disabled={isSelf || busy}
                        onChange={(e) => patch(u, { role: e.target.value as UserRole })}
                        className="rounded-lg border border-border-default bg-surface-raised px-2 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ROLE_ORDER.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                      {rowError?.id === u.id && <div className="mt-1 text-xs text-red-600">{rowError.msg}</div>}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        disabled={isSelf || busy}
                        onClick={() => patch(u, { is_active: !u.isActive })}
                        className={`rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                          u.isActive
                            ? 'bg-dcvfd-accent/15 text-dcvfd-dark hover:bg-dcvfd-accent/25'
                            : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'
                        }`}
                        title={u.isActive ? 'Click to deactivate' : 'Click to reactivate'}
                      >
                        {u.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-zinc-500">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'never'}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
